/* EV Before & After: the card scroller.

   The track is a native scroll-snap row, so swiping, trackpads, keyboard focus
   and the scrollbar all work with no script at all. This only adds the arrows
   and the mobile dots on top, and reads state back off scrollLeft rather than
   keeping its own index, so a user swipe and an arrow click can never disagree.

   When every card already fits, the whole control set is hidden rather than
   left present but dead. */

class EvBeforeAfter extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ba-track]');
    if (!this.track) return;

    /* data-static is read by CSS on the section, which carries the colour
       scheme classes, so it is set there rather than on this element. */
    this.root = this.closest('.ev-ba') || this;

    this.cards = Array.from(this.track.children);
    if (!this.cards.length) return;

    this.dots = Array.from(this.querySelectorAll('[data-ba-dot]'));
    this.prevButtons = Array.from(this.querySelectorAll('[data-ba-prev]'));
    this.nextButtons = Array.from(this.querySelectorAll('[data-ba-next]'));

    this.prevButtons.forEach((button) => button.addEventListener('click', () => this.step(-1)));
    this.nextButtons.forEach((button) => button.addEventListener('click', () => this.step(1)));
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));

    this.sync = this.sync.bind(this);
    this.onScroll = this.onScroll.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    /* Card widths are percentage based, so a resize changes how many fit and
       therefore whether the controls are needed at all. */
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(this.sync);
      this.observer.observe(this.track);
    } else {
      window.addEventListener('resize', this.sync);
    }

    this.sync();
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener('resize', this.sync);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  /* One card plus one gap, measured rather than assumed, so the gap and the
     card width can change at any breakpoint without touching this. */
  stride() {
    if (this.cards.length > 1) {
      const delta = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
      if (delta > 0) return delta;
    }
    return this.cards[0].getBoundingClientRect().width;
  }

  step(direction) {
    this.track.scrollBy({ left: direction * this.stride(), behavior: 'smooth' });
  }

  goTo(index) {
    const card = this.cards[index];
    if (!card) return;
    this.track.scrollTo({
      left: card.offsetLeft - this.cards[0].offsetLeft,
      behavior: 'smooth',
    });
  }

  onScroll() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.sync();
    });
  }

  sync() {
    const scrollable = this.track.scrollWidth - this.track.clientWidth;

    /* A 1px tolerance: fractional layout widths mean scrollLeft rarely lands
       exactly on either end. */
    this.root.toggleAttribute('data-static', scrollable <= 1);

    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= scrollable - 1;

    this.prevButtons.forEach((button) => {
      button.disabled = atStart;
    });
    this.nextButtons.forEach((button) => {
      button.disabled = atEnd;
    });

    if (!this.dots.length) return;

    const origin = this.cards[0].offsetLeft;
    const position = this.track.scrollLeft;
    let active = 0;
    let closest = Infinity;

    this.cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft - origin - position);
      if (distance < closest) {
        closest = distance;
        active = index;
      }
    });

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-current', index === active ? 'true' : 'false');
    });
  }
}

if (!customElements.get('ev-before-after')) {
  customElements.define('ev-before-after', EvBeforeAfter);
}
